/**
 * Interactive layer drawn INSIDE the scaled preview stage (so every pixel is
 * stage px). Drag moves a widget, the 8 handles resize it, both snap to the
 * grid / title-safe area / stage edges. Pointer capture is scoped to the
 * gesture's pointerId; pointercancel and unmount end the gesture.
 */
import { useCallback, useRef, useState } from "react";
import type { BroadcastLayout } from "../../lib/broadcastApi";
import {
  moveRect,
  resizeRect,
  titleSafeRect,
  type Guides,
  type Rect,
  type ResizeHandle,
} from "./editorMath";
import { STAGE_SIZES } from "./stageScale";

interface Props {
  layout: BroadcastLayout;
  /** Screen px per stage px (the preview's CSS scale). */
  scale: number;
  selectedId: string | null;
  snap: boolean;
  onSelect: (id: string | null) => void;
  onRect: (id: string, rect: Rect) => void;
}

interface Gesture {
  id: string;
  pointerId: number;
  mode: "move" | ResizeHandle;
  startX: number;
  startY: number;
  origin: Rect;
}

const HANDLES: Array<{
  h: ResizeHandle;
  left: string;
  top: string;
  cursor: string;
}> = [
  { h: "nw", left: "0%", top: "0%", cursor: "nwse-resize" },
  { h: "n", left: "50%", top: "0%", cursor: "ns-resize" },
  { h: "ne", left: "100%", top: "0%", cursor: "nesw-resize" },
  { h: "e", left: "100%", top: "50%", cursor: "ew-resize" },
  { h: "se", left: "100%", top: "100%", cursor: "nwse-resize" },
  { h: "s", left: "50%", top: "100%", cursor: "ns-resize" },
  { h: "sw", left: "0%", top: "100%", cursor: "nesw-resize" },
  { h: "w", left: "0%", top: "50%", cursor: "ew-resize" },
];

export default function EditorOverlay({
  layout,
  scale,
  selectedId,
  snap,
  onSelect,
  onRect,
}: Props) {
  const gesture = useRef<Gesture | null>(null);
  const [guides, setGuides] = useState<Guides>({ x: [], y: [] });
  const aspect = layout.stage.aspect;
  const dims = STAGE_SIZES[aspect];
  const safe = titleSafeRect(aspect);
  const s = scale > 0 ? scale : 1;
  const handlePx = 14 / s;

  const begin = (
    e: React.PointerEvent<HTMLElement>,
    id: string,
    mode: Gesture["mode"],
    origin: Rect,
  ) => {
    e.stopPropagation();
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    gesture.current = {
      id,
      pointerId: e.pointerId,
      mode,
      startX: e.clientX,
      startY: e.clientY,
      origin,
    };
    onSelect(id);
  };

  const move = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      const g = gesture.current;
      if (!g || e.pointerId !== g.pointerId) return;
      const dx = (e.clientX - g.startX) / s;
      const dy = (e.clientY - g.startY) / s;
      const out =
        g.mode === "move"
          ? moveRect(g.origin, dx, dy, aspect, { snap })
          : resizeRect(g.origin, g.mode, dx, dy, aspect, { snap });
      setGuides(out.guides);
      onRect(g.id, out.rect);
    },
    [s, aspect, snap, onRect],
  );

  const end = useCallback((e: React.PointerEvent<HTMLElement>) => {
    const g = gesture.current;
    if (!g || e.pointerId !== g.pointerId) return;
    gesture.current = null;
    setGuides({ x: [], y: [] });
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  }, []);

  return (
    <div
      data-testid="editor-overlay"
      onPointerDown={() => onSelect(null)}
      style={{ position: "absolute", inset: 0, zIndex: 1000 }}
    >
      <div
        data-testid="editor-safe-area"
        style={{
          position: "absolute",
          left: safe.x,
          top: safe.y,
          width: safe.w,
          height: safe.h,
          border: `${2 / s}px dashed rgba(250, 204, 21, 0.7)`,
          pointerEvents: "none",
        }}
      />
      {guides.x.map((x) => (
        <div
          key={`gx${x}`}
          data-testid="editor-guide-x"
          style={{
            position: "absolute",
            left: x,
            top: 0,
            width: 2 / s,
            height: dims.h,
            background: "#f43f5e",
            pointerEvents: "none",
          }}
        />
      ))}
      {guides.y.map((y) => (
        <div
          key={`gy${y}`}
          data-testid="editor-guide-y"
          style={{
            position: "absolute",
            top: y,
            left: 0,
            height: 2 / s,
            width: dims.w,
            background: "#f43f5e",
            pointerEvents: "none",
          }}
        />
      ))}

      {layout.widgets.map((w) => {
        const selected = w.id === selectedId;
        const rect: Rect = { x: w.x, y: w.y, w: w.w, h: w.h };
        // Full-stage layers (call-outs) must not swallow every click; they
        // are selected from the layer list instead.
        const passThrough = w.type === "callouts" && !selected;
        return (
          <div
            key={w.id}
            data-testid={`editor-box-${w.id}`}
            data-selected={selected ? "true" : "false"}
            onPointerDown={
              passThrough ? undefined : (e) => begin(e, w.id, "move", rect)
            }
            onPointerMove={move}
            onPointerUp={end}
            onPointerCancel={end}
            style={{
              position: "absolute",
              left: w.x,
              top: w.y,
              width: w.w,
              height: w.h,
              boxSizing: "border-box",
              outline: `${(selected ? 3 : 1.5) / s}px ${
                w.visible ? "solid" : "dashed"
              } ${selected ? "#38bdf8" : "rgba(148,163,184,0.8)"}`,
              cursor: passThrough ? "default" : "move",
              pointerEvents: passThrough ? "none" : "auto",
              touchAction: "none",
              background: selected ? "rgba(56,189,248,0.08)" : "transparent",
            }}
          >
            {selected &&
              HANDLES.map((hd) => (
                <div
                  key={hd.h}
                  data-testid={`editor-handle-${hd.h}`}
                  onPointerDown={(e) => begin(e, w.id, hd.h, rect)}
                  onPointerMove={move}
                  onPointerUp={end}
                  onPointerCancel={end}
                  style={{
                    position: "absolute",
                    left: hd.left,
                    top: hd.top,
                    width: handlePx,
                    height: handlePx,
                    transform: "translate(-50%, -50%)",
                    background: "#38bdf8",
                    border: `${2 / s}px solid #0b1120`,
                    cursor: hd.cursor,
                    touchAction: "none",
                  }}
                />
              ))}
          </div>
        );
      })}
    </div>
  );
}
